import { useState } from "react";
import { Editor, Transforms } from "slate";
import { CustomElement } from "../../../../../slate"; // Adjust import path as needed
import { ReactEditor } from "slate-react";

export interface SlateElementEditData<T extends Record<string, any>> {
  editingContext: { editor: Editor, element: CustomElement } | null;
  elementEdit: Partial<T>;
  update: (changes: Partial<T>) => void;
  startEditing: (editor: Editor, element: CustomElement) => void;
  completeEditing: () => void;
  cancel: () => void;
}

/**
 * A generic hook to manage the editing state of a custom Slate element via a dialog.
 * 
 * @param mapElement A function to extract the relevant editable fields from the Slate CustomElement.
 */
export const useSlateElementEdit = <T extends Record<string, any>>(
  mapElement: (element: CustomElement) => T
): SlateElementEditData<T> => {
  const [editingContext, setEditingContext] = useState<{ editor: Editor, element: CustomElement } | null>(null);
  const [elementEdit, setElementEdit] = useState<Partial<T>>({});

  const startEditing = (editor: Editor, element: CustomElement) => {
    setEditingContext({ editor, element });
    setElementEdit(mapElement(element));
  };

  const close = () => {
    setEditingContext(null);
    setElementEdit({});
  };

  const completeEditing = () => {
    if (!editingContext) {
      return;
    }
    const { element, editor } = editingContext;
    
    if (element && editor) {
      try {
        const path = ReactEditor.findPath(editor, element);
        Transforms.setNodes(
          editor,
          elementEdit as any,
          { at: path }
        );
      } catch (e) {
        console.error("Failed to update Slate node. It may have been removed from the document.", e);
      }
    }
    close();
  };

  const update = (changes: Partial<T>) => {
    setElementEdit(prev => ({ ...prev, ...changes }));
  };

  return {
    editingContext,
    elementEdit,
    update,
    startEditing,
    completeEditing,
    cancel: close,
  };
}
